/**
 *
 * When the link is clicked on it opens a dialog element to the video embed.
 * This is much simpler than using something like colorbox.
 *
 */
import {onDocumentLoaded} from "@plaudit/frontend-apis";

type VideoModalsArgs = {
	customPlayButton?: string|false,
	anchorSelector?: string
};
export function videoModals(config: VideoModalsArgs) {
	onDocumentLoaded(() => {
		const VIDEO_DOMAINS = ["vimeo.com", "youtube.com", "youtu.be"];
		const anchorSelector = config.anchorSelector;
		if (anchorSelector) {
			// On load, we wire up existing anchors with media links
			for (const element of document.body.querySelectorAll<HTMLAnchorElement>(anchorSelector)) {
				console.log(element);
				if (element.href && !element.target && VIDEO_DOMAINS.find(domain => element.href.includes(domain))) {
					wireUpAnchorElement(element, config?.customPlayButton);
				}
			}

			// This makes sure that AJAX doesn't break the popups
			new MutationObserver(mutations => {
				for (const mutation of mutations) {
					if (mutation.addedNodes?.length) {
						// On load, we wire up existing anchors with media links
						for (const element of document.body.querySelectorAll<HTMLAnchorElement>(anchorSelector)) {
							if (element.href && !element.target && !element.classList.contains("attached-video-modal") && VIDEO_DOMAINS.find(domain => element.href.includes(domain))) {
								wireUpAnchorElement(element, config?.customPlayButton);
							}
						}
						break;
					}
				}
			}).observe(document.body, {childList: true, subtree: true});
		} else {
			const anchorQuery = `a:where(${VIDEO_DOMAINS.map(domain => `[href*="${domain}"]`).join(", ")}):not([target])`;

			// On load, we wire up existing anchors with media links
			for (const element of document.body.querySelectorAll<HTMLAnchorElement>(anchorQuery)) {
				wireUpAnchorElement(element, config?.customPlayButton);
			}

			// This makes sure that AJAX doesn't break the popups
			new MutationObserver(mutations => {
				for (const mutation of mutations) {
					for (const addedNode of mutation.addedNodes) {
						if (addedNode instanceof HTMLAnchorElement) { // If an anchor is added, we can just check it directly
							if (addedNode.href && VIDEO_DOMAINS.find(domain => addedNode.href.includes(domain))) {
								wireUpAnchorElement(addedNode, config?.customPlayButton);
							}
						} else if (addedNode instanceof Element) { // If any other element is added, we need to query within it
							for (const element of addedNode.querySelectorAll<HTMLAnchorElement>(anchorQuery)) {
								wireUpAnchorElement(element, config?.customPlayButton);
							}
						}
					}
				}
			}).observe(document.body, {childList: true, subtree: true});
		}
	});
}

function addCustomPlayButton(element: HTMLAnchorElement, customPlayButton: string) {
	const span = document.createElement('span');
	span.classList.add('custom-play-button');
	span.innerHTML = customPlayButton;
	element.classList.add('has-custom-play');
	element.setAttribute('aria-label', 'Link to Video')
	element.appendChild(span);
}

function wireUpAnchorElement(element: HTMLAnchorElement, customPlayButton?: unknown) {
	if (element.classList.contains("attached-video-modal")) {
		return;
	}
	element.classList.add("attached-video-modal");

	//* DEBUG */console.log(element);
	if (element.querySelector('img')) {
		element.classList.add('has-play-button');
		if (typeof customPlayButton === 'string') {
			element.classList.add('has-play-button--custom');
			addCustomPlayButton(element, customPlayButton);
		} else {
			element.classList.add('has-play-button--basic');
		}
	}
	element.addEventListener('click', event => {
		if (openVideoDialog(element)) {
			event.preventDefault();
		}
	});
}

function openVideoDialog(element: HTMLAnchorElement) {
	//* DEBUG */console.log('openVideoDialog', href);
	const url = new URL(element.href);
	// Create the dialog element using a template literal
	const dialog = document.createElement('dialog');
	dialog.classList.add('dialog-video');
	dialog.innerHTML = `
        <form method="dialog">
            <button autofocus><span class="screen-reader-text">Close</span> <i class="icon"></i></button>
        </form>
    `;

	const iframe = document.createElement('iframe');
	iframe.allow = 'autoplay';
	iframe.width = '560';
	iframe.height = '315';
	if (url.hostname.endsWith('vimeo.com')) {
		url.hostname = "player.vimeo.com";
		url.pathname = "/video" + url.pathname;
		iframe.src = url.toString();
		iframe.width = '500';
		iframe.height = '281';
	} else if (url.hostname.endsWith('youtube.com')) {
		const convertedURL = url.pathname.startsWith("/embed/") ? url : convertYouTubeURLToEmbeddableForm(url, false);
		if (!convertedURL) {
			return false;
		}
		iframe.src = convertedURL.toString();
	} else if (url.hostname.endsWith('youtu.be')) {
		const convertedURL = convertYouTubeURLToEmbeddableForm(url, true);
		if (!convertedURL) {
			return false;
		}
		iframe.src = convertedURL.toString();
	} else {
		console.error(`Attempted to open a dialog for an unsupported URL: ${url}`);
		return false;
	}

	dialog.appendChild(iframe);
	dialog.addEventListener("close", () => {
		dialog.remove();
		element.focus()
	});

	// Append the dialog to the body and show it
	document.body.appendChild(dialog);
	dialog.showModal();
	return true;
}
function convertYouTubeURLToEmbeddableForm(url: URL, short: boolean): URL|undefined {
	if (short) {
		if (url.pathname.length < 10) {
			console.error(`Attempted to open a dialog for an unsupported URL: ${url}`);
			return undefined;
		}
		url.pathname = "/embed" + url.pathname;
		url.hostname = "www.youtube.com";
	} else {
		const videoId = url.searchParams.get("v");
		if (!videoId) {
			console.error(`Attempted to open a dialog for an unsupported URL: ${url}`);
			return undefined;
		}
		url.pathname = "/embed/" + videoId;
		url.searchParams.delete("v");
	}
	url.searchParams.delete("si");
	return url;
}
